/**
 * AppAccessConfig — access-control panel for a managed app's public URL.
 *
 * The analog of the pipeline's WebhookAuthConfig, but the data model differs.
 * A managed app is reached through a BROWSER (its public URL), so the only
 * gates that make sense are HTTP Basic auth (the native browser prompt) and an
 * IP allowlist. A Bearer token is intentionally NOT offered here: you can't set
 * an `Authorization: Bearer …` header on a browser navigation, so it would only
 * lock the user out of their own app's UI. (The `PATCH /apps/:id/auth` backend
 * still supports authType:'token' — we just don't surface it in this UI.)
 *
 * Reuses the shared <AuthCard> atomic and the pipeline-auth-* styles
 * (PipelineDetailPage.css is imported by the app detail page too).
 *
 * While the app is RUNNING, every change rolls the ECS task (~1 min) so the
 * Caddy sidecar comes up alongside it (backend returns 202). While STOPPED the
 * change is just persisted on the row and applied on the next Start (200) —
 * the panel stays editable either way; only a DESTROYED app is read-only.
 */

import React, { useState } from 'react';
import { IonToggle } from '@ionic/react';
import AuthCard from '../../components/shared/AuthCard/AuthCard';
import PillButton from '../../components/shared/PillButton';
import { Apps } from '../../services/apps';

// The server never sends the real Basic-auth password back (it's stored
// hashed). When one is already on file we pre-fill the field with this sentinel
// so it RENDERS as filled (dots) instead of an empty box — the user can see a
// password is set. It's cleared the moment they focus to type a new one, and
// treated as "unchanged" on save (never sent to the server).
const PW_SENTINEL = '••••••••';

const AppAccessConfig = ({ app, onChanged, setToast }) => {
  const authStatus = app?.authStatus || { type: 'none' };
  const mode = authStatus.type || 'none';
  // A merely-stopped/failed app keeps its EFS data and can be configured: the
  // backend persists the change on the row and applies it on the next Start
  // (no running task to roll). Only a DESTROYED app (EFS wiped) is read-only.
  const destroyed = !!app?.destroyedAt || app?.status === 'destroyed';
  const isRunning = app?.status === 'running';
  // The toast copy + backend behaviour differ when the app isn't running:
  // running → task rolls now (~1 min); stopped → saved, applied next start.
  const savedSuffix = isRunning ? ' — task restarting (~1 min)' : ' — applied next start';

  // Does the SERVER currently enforce any gate? Drives the master toggle's
  // initial position + whether turning it off needs a clear-all PATCH.
  const serverHasGate = mode === 'basic' || (Array.isArray(authStatus.ipAllowList) && authStatus.ipAllowList.length > 0);

  // ── Master "Authentication" switch (mirrors the pipeline webhook header
  // toggle). ON reveals the Basic/IP cards; OFF means the app is public.
  // Turning it OFF while a gate is live clears everything in one PATCH.
  const [gateOn, setGateOn] = useState(serverHasGate);
  const [togglingGate, setTogglingGate] = useState(false);

  // Basic
  const [basicEnabled, setBasicEnabled] = useState(mode === 'basic');
  const [basicUsername, setBasicUsername] = useState(authStatus.user || '');
  // Pre-fill with the sentinel when a password is already set so the field
  // shows as filled (dots) on load.
  const [basicPassword, setBasicPassword] = useState(mode === 'basic' ? PW_SENTINEL : '');
  const [savingBasic, setSavingBasic] = useState(false);
  // Persistent inline error (red text in the card) so a failed save is NEVER
  // silent — a transient toast was getting missed. Cleared on the next attempt.
  const [basicError, setBasicError] = useState('');
  const passwordOnFile = mode === 'basic';

  // IP allowlist — INDEPENDENT of basic/token (an app can have basic auth AND
  // an IP gate). Backed by authStatus.ipAllowList (array of CIDRs).
  const ipList = Array.isArray(authStatus.ipAllowList) ? authStatus.ipAllowList : [];
  const [ipEnabled, setIpEnabled] = useState(ipList.length > 0);
  const [ipText, setIpText] = useState(ipList.join('\n'));
  const [savingIp, setSavingIp] = useState(false);
  const [ipError, setIpError] = useState('');

  const patchAuth = async (body) => {
    const res = await Apps.updateAuth(app.instanceId, body);
    // Refresh page state so authStatus (the masked source of truth) updates.
    onChanged?.();
    return res;
  };

  // ── Master gate switch ───────────────────────────────────────────────
  const handleToggleGate = async (on) => {
    if (on) { setGateOn(true); return; } // reveal cards; no request yet
    // Turning OFF. If nothing is enforced server-side, just collapse the UI.
    if (!serverHasGate) {
      setGateOn(false);
      setBasicEnabled(false);
      setIpEnabled(false);
      return;
    }
    // A live gate exists → clear EVERYTHING in one PATCH so the app is public.
    setTogglingGate(true);
    try {
      await patchAuth({ authType: 'none', ipAllowList: [] });
      setGateOn(false);
      setBasicEnabled(false);
      setIpEnabled(false);
      setIpText('');
      setBasicPassword('');
      setToast?.({ isOpen: true, message: `Authentication off — app is public${savedSuffix}`, color: 'success' });
    } catch (err) {
      setGateOn(true);
      setToast?.({ isOpen: true, message: `Could not disable authentication: ${err.message || 'error'}`, color: 'danger' });
    } finally {
      setTogglingGate(false);
    }
  };

  // ── Basic ────────────────────────────────────────────────────────────
  const handleToggleBasic = async (on) => {
    if (on) {
      // Expand to collect creds; Save commits.
      setBasicEnabled(true);
      return;
    }
    // Off, but Basic was only opened locally (server gate is token/none) →
    // just cancel the form; don't PATCH (that would clobber a live token gate).
    if (mode !== 'basic') {
      setBasicEnabled(false);
      setBasicPassword('');
      return;
    }
    // Off and server actually has basic → clear the gate entirely.
    setSavingBasic(true);
    try {
      await patchAuth({ authType: 'none' });
      setBasicEnabled(false);
      setToast?.({ isOpen: true, message: `Basic auth removed${savedSuffix}`, color: 'success' });
    } catch (err) {
      setBasicEnabled(true);
      setToast?.({ isOpen: true, message: `Could not disable Basic auth: ${err.message || 'error'}`, color: 'danger' });
    } finally {
      setSavingBasic(false);
    }
  };

  const handleSaveBasic = async () => {
    setBasicError('');
    // Minimal validation only — username + a password (any length) for a
    // first-time setup. No length rule: the user wants any password to work.
    if (!basicUsername.trim()) {
      setBasicError('Enter a username.');
      return;
    }
    // The password we'd actually send: a typed value that isn't the
    // "unchanged" sentinel. Empty (or untouched sentinel) means "keep existing".
    const pwToSend = (basicPassword && basicPassword !== PW_SENTINEL) ? basicPassword : '';
    if (!passwordOnFile && !pwToSend) {
      setBasicError('Enter a password.');
      return;
    }
    setSavingBasic(true);
    try {
      const body = { authType: 'basic', authUser: basicUsername.trim() };
      // Only send a password if the user actually typed one — an untouched
      // sentinel (or blank) means "keep the existing password".
      if (pwToSend) body.authPassword = pwToSend;
      await patchAuth(body);
      // A password is now on file → show the field as filled again.
      setBasicPassword(PW_SENTINEL);
      setToast?.({ isOpen: true, message: `Basic auth saved${savedSuffix}`, color: 'success' });
    } catch (err) {
      // NEVER silent — surface the backend reason inline (persists) AND toast.
      const msg = err.message || 'Save failed';
      setBasicError(msg);
      setToast?.({ isOpen: true, message: `Could not save Basic auth: ${msg}`, color: 'danger' });
    } finally {
      setSavingBasic(false);
    }
  };

  // ── IP allowlist (independent) ───────────────────────────────────────
  // Whether the SERVER currently enforces an IP gate. Toggling the card on/off
  // is purely local UNLESS a real gate exists — no wasted PATCH for "open then
  // close without ever saving". Mirrors handleToggleBasic's mode check.
  const ipGateActive = ipList.length > 0;
  const handleToggleIp = async (on) => {
    if (on) { setIpEnabled(true); return; } // expand; Save commits the list
    // Off, but nothing was ever saved → just collapse, no request.
    if (!ipGateActive) {
      setIpEnabled(false);
      setIpText('');
      return;
    }
    // Off and the server actually has an IP gate → clear it.
    setSavingIp(true);
    try {
      await patchAuth({ ipAllowList: [] }); // clear the IP gate
      setIpEnabled(false);
      setIpText('');
      setToast?.({ isOpen: true, message: `IP allowlist removed${savedSuffix}`, color: 'success' });
    } catch (err) {
      setIpEnabled(true);
      setToast?.({ isOpen: true, message: `Could not disable IP allowlist: ${err.message || 'error'}`, color: 'danger' });
    } finally { setSavingIp(false); }
  };
  const ipEntries = ipText.split('\n').map((l) => l.trim()).filter(Boolean);
  const handleSaveIp = async () => {
    setIpError('');
    if (!ipEntries.length) {
      setIpError('Add at least one IP / CIDR.');
      return;
    }
    setSavingIp(true);
    try {
      await patchAuth({ ipAllowList: ipEntries });
      setToast?.({ isOpen: true, message: `IP allowlist saved${savedSuffix}`, color: 'success' });
    } catch (err) {
      const msg = err.message || 'Save failed';
      setIpError(msg);
      setToast?.({ isOpen: true, message: `Could not save IP allowlist: ${msg}`, color: 'danger' });
    } finally { setSavingIp(false); }
  };

  return (
    <div className="pipeline-section pipeline-section--separated">
      {/* Header: title left, master switch right — mirrors the pipeline
          "Trigger via webhook" section header exactly. */}
      <div className="pipeline-webhook-header">
        <h3 className="pipeline-section-title pipeline-webhook-header-title">
          Authentication
        </h3>
        <IonToggle
          className="pipeline-auth-switch"
          checked={gateOn}
          disabled={destroyed || togglingGate}
          onIonChange={(e) => {
            if (e.detail.checked !== gateOn) handleToggleGate(e.detail.checked);
          }}
          aria-label={gateOn ? 'Disable authentication' : 'Enable authentication'}
        />
      </div>

      {destroyed ? (
        <p className="pipeline-section-note pipeline-webhook-disabled-note">
          This app has been destroyed — access control can’t be changed.
        </p>
      ) : !gateOn ? (
        <p className="pipeline-section-note pipeline-webhook-disabled-note">
          This app is public — anyone with the URL can open it. Turn on to
          require a login or restrict by IP.
        </p>
      ) : (
        <p className="pipeline-section-note">
          Gate this app’s public URL with HTTP Basic auth or an IP allowlist.
          Either can be on independently. Changes restart the app (~1 min)
          while the auth proxy comes up.
        </p>
      )}

      {!destroyed && gateOn && (
      <div className="pipeline-auth-cards">
        {/* ── Basic auth ───────────────────────────────────────────────── */}
        <AuthCard
          name="Basic auth"
          description="Require an HTTP Basic username + password to reach the app."
          enabled={basicEnabled}
          onToggle={handleToggleBasic}
          toggling={savingBasic}
        >
          <input
            type="text"
            className="pipeline-env-add-input"
            placeholder="Username"
            value={basicUsername}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            onChange={(e) => setBasicUsername(e.target.value)}
            aria-label="Basic auth username"
          />
          <input
            type="password"
            className="pipeline-env-add-input"
            placeholder="Password"
            value={basicPassword}
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            onFocus={() => { if (basicPassword === PW_SENTINEL) setBasicPassword(''); }}
            onChange={(e) => setBasicPassword(e.target.value)}
            aria-label="Basic auth password"
          />
          {basicError && (
            <p className="pipeline-auth-hint" style={{ color: '#ff6b6b' }} role="alert">
              {basicError}
            </p>
          )}
          <div className="pipeline-auth-block-actions">
            <PillButton loading={savingBasic} onClick={handleSaveBasic}>
              Save
            </PillButton>
          </div>
        </AuthCard>

        {/* ── IP allowlist (independent of Basic) ──────────────────────── */}
        <AuthCard
          name="IP allowlist"
          description="Only accept requests from these IPs / CIDRs."
          enabled={ipEnabled}
          onToggle={handleToggleIp}
          toggling={savingIp}
        >
          <textarea
            className="pipeline-env-add-textarea"
            rows={4}
            placeholder={'One IP or CIDR per line, e.g.\n203.0.113.7\n198.51.100.0/24\n2001:db8::/32'}
            value={ipText}
            onChange={(e) => setIpText(e.target.value)}
            aria-label="Allowed IPs and CIDR ranges"
          />
          {ipError && (
            <p className="pipeline-auth-hint" style={{ color: '#ff6b6b' }} role="alert">
              {ipError}
            </p>
          )}
          <div className="pipeline-auth-block-actions">
            <PillButton loading={savingIp} onClick={handleSaveIp}>
              Save
            </PillButton>
          </div>
        </AuthCard>
      </div>
      )}
    </div>
  );
};

export default AppAccessConfig;
